/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Robot, Lightning, Calendar, Sparkle, PlayCircle,
  CheckCircle, XCircle, TrendUp, Article as ArticleIcon, ArrowsClockwise,
  Hash, FileText, Clock, ChartLine, Star,
} from "@phosphor-icons/react";
import AutoBlogAnalyticsTab from "../components/AutoBlogAnalyticsTab";

// Same SLATE theme as the manual blog admin — visually paired tools.
const TH = {
  bg: "#0F1623", surface: "#172234", surfaceHi: "#1F2D45",
  border: "#2B3A56", borderHi: "#3A4E72",
  accent: "#F5C16C", accentDim: "#8C6638",
  text: "#EAF0FA", textMute: "#90A1BD", textDim: "#566A8A",
  green: "#4ADE9C", red: "#FB7185", purple: "#A78BFA",
};

const STATUS_COLOR = {
  planned: TH.textMute,
  writing: TH.purple,
  published: TH.green,
  skipped: TH.textDim,
  duplicate: TH.textDim,
  error: TH.red,
};

function fmtCountdown(secs) {
  if (!Number.isFinite(secs) || secs <= 0) return "now";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtVolume(v) {
  if (v == null) return "—";
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k/mo`;
  return `${v}/mo`;
}

// Week badge — week 1 = today's row, week 2 = days 8-14 out, etc.
// Matches RankAI's `Week N` chips, but anchored to the plan's earliest date.
function weekFromIndex(idx) {
  return Math.floor(idx / 7) + 1;
}

export default function AdminAutoBlogPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("upcoming");  // 'ready' | 'upcoming'
  const [topTab, setTopTab] = useState("plan"); // 'plan' | 'analytics'

  const refresh = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/super-admin/autoblog/status"),
        api.get("/super-admin/autoblog/plan"),
      ]);
      setStatus(s.data);
      setPlan(p.data?.plan || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load autoblog state");
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user?.is_super_admin) { navigate("/"); return; }
    refresh();
  }, [user, loading, navigate, refresh]);

  const toggleAutoBlog = async () => {
    const next = !status?.enabled;
    setBusy("toggle");
    try {
      await api.post("/super-admin/autoblog/toggle", { enabled: next });
      toast.success(next ? "Auto-blog is now ON" : "Auto-blog turned OFF");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
    finally { setBusy(""); }
  };

  const fetchKeywords = async () => {
    setBusy("keywords");
    try {
      const { data } = await api.get("/super-admin/autoblog/keywords");
      setKeywords(data?.keywords || []);
      toast.success(`Fetched ${data?.count || 0} trending astrology keywords.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch keywords"); }
    finally { setBusy(""); }
  };

  const generatePlan = async () => {
    if (!window.confirm("Generate a fresh 120-day plan from your Ubersuggest keywords? You'll see ~30 rows immediately and the remaining 90 fill in over the next minute. ~₹0.80 total. Pending (unpublished) rows will be replaced.")) return;
    setBusy("plan");
    try {
      const { data } = await api.post("/super-admin/autoblog/plan");
      toast.success(`Planned ${data?.count || 0} blog posts so far — the rest fill in over the next minute.`);
      await refresh();
      setTimeout(refresh, 25000);
      setTimeout(refresh, 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Plan generation failed"); }
    finally { setBusy(""); }
  };

  const runNow = async () => {
    if (!window.confirm("Write & publish the next planned post now? (~30 seconds + ~₹0.20 LLM cost.)")) return;
    setBusy("run");
    try {
      const { data } = await api.post("/super-admin/autoblog/run-now");
      if (data?.ok) toast.success(`Published: ${data.title}`);
      else toast.warning(data?.reason || "Nothing planned to publish.");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy(""); }
  };

  const skipPlan = async (plan_id) => {
    if (!window.confirm("Skip this scheduled post?")) return;
    try {
      await api.delete(`/super-admin/autoblog/plan/${plan_id}`);
      toast.success("Skipped.");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Skip failed"); }
  };

  // Bucket planned rows by tab: ready (today/overdue), upcoming
  // (future-scheduled normal plan), and low_kd (the unscheduled
  // easy-win pool).
  const { readyRows, upcomingRows, lowKdRows, customRows } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ready = [];
    const upcoming = [];
    const lowKd = [];
    const custom = [];
    plan.forEach((row) => {
      if (row.priority === "custom") { custom.push(row); return; }
      if (row.status !== "planned") return;
      if (row.priority === "low_kd") { lowKd.push(row); return; }
      if (row.scheduled_date && row.scheduled_date <= today) ready.push(row);
      else upcoming.push(row);
    });
    // Newest custom rows first — admin usually wants to see the one they just typed.
    custom.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return { readyRows: ready, upcomingRows: upcoming, lowKdRows: lowKd, customRows: custom };
  }, [plan]);

  const rowsForTab =
    tab === "ready" ? readyRows :
    tab === "lowkd" ? lowKdRows :
    tab === "custom" ? customRows :
    upcomingRows;

  // Multi-select state — keyed by plan_id. Reset whenever the active
  // tab changes so a stale checkbox can't survive a tab switch.
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [tab]);
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllInTab = () => {
    setSelected((prev) => {
      const allIds = rowsForTab.map((r) => r.plan_id);
      const allOn = allIds.every((id) => prev.has(id));
      const next = new Set(prev);
      allIds.forEach((id) => { if (allOn) next.delete(id); else next.add(id); });
      return next;
    });
  };

  const bulkPublish = async (mode) => {
    // mode = "selected" | "tab" | "all"
    let ids = [];
    let allFlag = false;
    if (mode === "selected") ids = Array.from(selected);
    else if (mode === "tab") ids = rowsForTab.map((r) => r.plan_id);
    else allFlag = true;
    const n = allFlag ? scheduledCount : ids.length;
    if (n === 0) { toast.warning("Nothing selected."); return; }
    const cost = (n * 0.20).toFixed(2);
    const eta = Math.ceil(n * 35 / 60);
    if (!window.confirm(`Publish ${n} post${n === 1 ? "" : "s"} now?\n\nEstimated cost: ₹${cost}\nEstimated time: ~${eta} minutes (sequential).\n\nThe rows will flip to "published" one by one — reload to see progress.`)) return;
    setBusy("bulk");
    try {
      const { data: r } = await api.post("/super-admin/autoblog/publish-batch", {
        plan_ids: ids, all: allFlag,
      });
      if (r?.ok) {
        toast.success(`Queued ${r.queued} posts. ETA ~${Math.ceil(r.estimated_seconds / 60)} min.`);
        setSelected(new Set());
      } else {
        toast.warning(r?.reason || "Nothing to publish.");
      }
      // Refresh a few times so admin sees the queue drain.
      await refresh();
      setTimeout(refresh, 45000);
      setTimeout(refresh, 120000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk publish failed"); }
    finally { setBusy(""); }
  };

  const generateLowKd = async () => {
    if (!window.confirm("Generate a 300-row Low-KD candidate pool? (~₹2.40 LLM cost.) The first 25 rows return immediately; the remaining 275 are filled in the background over ~3 minutes — keep this tab open and the list will auto-refresh. Any prior low-KD pool will be replaced.")) return;
    setBusy("lowkd");
    try {
      const { data } = await api.post("/super-admin/autoblog/low-kd/generate");
      const initial = data?.count || 0;
      const queued = data?.background_queued || 0;
      toast.success(
        queued > 0
          ? `Inserted ${initial} low-KD candidates; ${queued} more are generating in the background. Switch to the "Low KD" tab — the list auto-refreshes.`
          : `Generated ${initial} low-KD candidates. Switch to the "Low KD" tab.`
      );
      setTab("lowkd");
      await refresh();
      if (queued > 0) {
        // Aggressive auto-refresh while background fills the pool.
        setTimeout(refresh, 30000);
        setTimeout(refresh, 75000);
        setTimeout(refresh, 130000);
        setTimeout(refresh, 200000);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Low-KD generation failed"); }
    finally { setBusy(""); }
  };

  // ── Custom-topic form state (the "Add Topic" tab) ──────────────────
  const [customTitle, setCustomTitle] = useState("");
  const [customAngle, setCustomAngle] = useState("");

  // ── AI-edit modal state — reused for any published row's "Edit" button ──
  const [editTarget, setEditTarget] = useState(null);   // {post_id, slug, title}
  const [editInstruction, setEditInstruction] = useState("");
  const openEdit = (row) => {
    setEditTarget({ post_id: row.post_id, slug: row.slug, title: row.title });
    setEditInstruction("");
  };
  const submitEdit = async () => {
    const inst = editInstruction.trim();
    if (inst.length < 8) { toast.warning("Tell me what to change (at least 8 chars)."); return; }
    if (!editTarget?.post_id) return;
    setBusy(`edit:${editTarget.post_id}`);
    try {
      const { data } = await api.post(
        `/super-admin/autoblog/edit/${editTarget.post_id}`,
        { instruction: inst },
      );
      const delta = data?.chars_delta ?? 0;
      toast.success(
        `Edited /blog/${data?.slug}${delta ? ` (${delta > 0 ? "+" : ""}${delta} chars)` : ""}. Open the page to verify.`,
      );
      setEditTarget(null);
      setEditInstruction("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
    } finally { setBusy(""); }
  };
  const publishCustomTopic = async (e) => {
    e?.preventDefault?.();
    const title = customTitle.trim();
    const angle = customAngle.trim();
    if (title.length < 15) { toast.warning("Title needs at least 15 characters."); return; }
    if (title.length > 140) { toast.warning("Title max 140 characters."); return; }
    if (!window.confirm(`Generate + publish "${title}"?\n\n~₹0.20 LLM cost, ~40s. It'll appear at the top of the Custom tab and then flip to "published" with a live URL when done.`)) return;
    setBusy("custom");
    try {
      const { data } = await api.post("/super-admin/autoblog/publish-topic", { title, angle });
      toast.success(`Queued. Watch the Custom tab — the URL will appear when Haiku finishes (~40s).`);
      setCustomTitle("");
      setCustomAngle("");
      setTab("custom");
      await refresh();
      // Poll for the plan_id becoming published so the admin sees the
      // "publishing…" pill flip to a clickable "/blog/…" link.
      const pid = data?.plan_id;
      if (pid) {
        const poll = async (attemptsLeft) => {
          if (attemptsLeft <= 0) return;
          try {
            const { data: row } = await api.get(`/super-admin/autoblog/plan-status/${pid}`);
            if (row?.status === "published") {
              toast.success(`Published → /blog/${row.slug}`);
              await refresh();
              return;
            }
            if (row?.status === "duplicate") {
              toast.warning(`Duplicate: an existing post already covers this title (→ /blog/${row.duplicate_of_slug}).`);
              await refresh();
              return;
            }
            if (row?.status === "error") {
              toast.error(`Generation failed: ${row.error || "unknown error"}`);
              await refresh();
              return;
            }
          } catch (_) { /* transient — keep polling */ }
          setTimeout(() => poll(attemptsLeft - 1), 6000);
        };
        setTimeout(() => poll(30), 8000);   // start after 8s, poll for ~3 min
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Custom topic publish failed");
    } finally { setBusy(""); }
  };

  if (loading || !user?.is_super_admin) return null;

  const enabled = !!status?.enabled;
  const keywordCorpus = status?.keyword_corpus ?? 0;
  const publishedCount = status?.published_count ?? 0;
  const scheduledCount = status?.plan_count ?? 0;

  return (
    <div style={{ background: TH.bg, color: TH.text, minHeight: "100vh" }}>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-autoblog-page">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" style={{ color: TH.textMute }} className="inline-flex items-center gap-2 text-sm hover:opacity-80" data-testid="autoblog-back-btn">
            <ArrowLeft size={16} /> Back to Super Admin
          </Link>
        </div>

        {/* Hero */}
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase" style={{ background: enabled ? TH.green + "20" : TH.surfaceHi, color: enabled ? TH.green : TH.textMute, border: `1px solid ${enabled ? TH.green : TH.border}` }}>
              <Robot size={12} weight="fill" /> Auto-Blog · {enabled ? "ACTIVE" : "PAUSED"}
            </div>
            <h1 className="text-3xl sm:text-4xl font-light" style={{ fontFamily: "Cormorant Garamond, serif", color: TH.text }}>
              Keywords & Content Strategy
            </h1>
            <p className="mt-2 text-sm max-w-2xl" style={{ color: TH.textMute }}>
              Ubersuggest-driven editorial pipeline → AstroKuberChat auto-publishes one SEO blog every day at <strong style={{ color: TH.accent }}>{status?.daily_run_hour_utc ?? 2}:00 UTC</strong> (07:30 IST). Style locked to the <Link to="/blog/how-to-find-a-verified-vedic-astrologer-online-safely" style={{ color: TH.accent }} target="_blank">"Verified Vedic Astrologer"</Link> reference piece.
            </p>
          </div>
          <button
            onClick={toggleAutoBlog}
            disabled={!!busy}
            className="px-5 py-3 rounded-full border-2 inline-flex items-center gap-2 transition-all disabled:opacity-50 text-sm font-bold tracking-wider uppercase"
            style={{ background: enabled ? TH.green + "15" : TH.surface, borderColor: enabled ? TH.green : TH.border, color: enabled ? TH.green : TH.text }}
            data-testid="autoblog-toggle-btn"
          >
            <Lightning size={16} weight="fill" /> {enabled ? "Auto-Publish ON" : "Auto-Publish OFF"}
          </button>
        </header>

        {/* RankAI-style summary cards */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="autoblog-stats">
          <div className="p-5 rounded-2xl border flex items-start gap-4" style={{ background: TH.surface, borderColor: TH.border }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: TH.purple + "20", color: TH.purple }}>
              <Hash size={24} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ background: TH.surfaceHi, color: TH.textMute }}>Keywords</div>
              <div className="mt-2 text-3xl font-semibold" data-testid="kw-corpus-count">{keywordCorpus.toLocaleString()}</div>
              <div className="mt-0.5 text-xs" style={{ color: TH.textMute }}>High-volume Ubersuggest seeds (vol ≥ 2k, KD ≤ 50)</div>
            </div>
          </div>
          <div className="p-5 rounded-2xl border flex items-start gap-4" style={{ background: TH.surface, borderColor: TH.border }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: TH.green + "20", color: TH.green }}>
              <FileText size={24} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ background: TH.green + "15", color: TH.green }}>Created</div>
              <div className="mt-2 text-3xl font-semibold" data-testid="pages-created-count">{publishedCount}</div>
              <div className="mt-0.5 text-xs" style={{ color: TH.textMute }}>Posts published so far — live at <Link to="/blog" target="_blank" style={{ color: TH.accent }}>/blog</Link></div>
            </div>
          </div>
          <div className="p-5 rounded-2xl border flex items-start gap-4" style={{ background: TH.surface, borderColor: TH.border }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: TH.accent + "20", color: TH.accent }}>
              <Clock size={24} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ background: TH.accent + "15", color: TH.accent }}>Scheduled</div>
              <div className="mt-2 text-3xl font-semibold" data-testid="pages-scheduled-count">{scheduledCount}</div>
              <div className="mt-0.5 text-xs" style={{ color: TH.textMute }}>Next run in <strong style={{ color: TH.text }}>{fmtCountdown(status?.next_run_in_seconds)}</strong> · {status?.last_run_status === "ok" ? <span style={{ color: TH.green }}>last run ok</span> : status?.last_run_status === "error" ? <span style={{ color: TH.red }}>last run failed</span> : "never run yet"}</div>
            </div>
          </div>
        </section>

        {/* Top-level tabs — Plan / Analytics */}
        <div className="flex items-center gap-1 mb-6 border-b" style={{ borderColor: TH.border }} data-testid="autoblog-toptabs">
          {[["plan", "Plan & Calendar"], ["analytics", "Analytics & Rewrites"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTopTab(id)}
              className="px-4 py-3 text-sm font-medium inline-flex items-center gap-2"
              style={{
                color: topTab === id ? TH.accent : TH.textMute,
                borderBottom: topTab === id ? `2px solid ${TH.accent}` : "2px solid transparent",
              }}
              data-testid={`toptab-${id}`}
            >
              {id === "plan" ? <Calendar size={14} weight="bold" /> : <ChartLine size={14} weight="bold" />}
              {label}
            </button>
          ))}
        </div>

        {topTab === "analytics" ? (
          <AutoBlogAnalyticsTab />
        ) : (
          <>

        {/* STEP 1 — Trending keywords */}
        <section className="mb-8 p-6 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="step1-keywords">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: TH.accent }}>Step 1</div>
              <h2 className="text-xl font-medium mt-1">Trending keyword preview</h2>
              <p className="text-xs mt-1" style={{ color: TH.textMute }}>Primary source: <strong style={{ color: TH.accent }}>Ubersuggest CSV</strong> ({keywordCorpus.toLocaleString()} keywords on disk). Live falls back to Reddit + DDG Autocomplete + curated seed expansion.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchKeywords} disabled={!!busy} className="px-4 py-2 rounded-full text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-50" style={{ background: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}` }} data-testid="fetch-keywords-btn">
                <TrendUp size={14} weight="bold" /> {busy === "keywords" ? "FETCHING…" : "PREVIEW TOP 60"}
              </button>
              <button onClick={generatePlan} disabled={!!busy} className="px-4 py-2 rounded-full text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-50" style={{ background: TH.accent, color: TH.bg }} data-testid="generate-plan-btn">
                <Sparkle size={14} weight="fill" /> {busy === "plan" ? "PLANNING…" : "GENERATE 120-DAY PLAN"}
              </button>
            </div>
          </div>
          {keywords.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
              {keywords.map((k, i) => (
                <div key={i} className="px-3 py-2 rounded-lg border text-xs flex items-center justify-between gap-2" style={{ background: TH.surfaceHi, borderColor: TH.border }}>
                  <span className="truncate" title={k.phrase}>{k.phrase}</span>
                  <span className="text-[10px] shrink-0" style={{ color: k.volume ? TH.accent : TH.textDim }}>{k.volume ? fmtVolume(k.volume) : k.source.split(":")[0].replace("ubersuggest", "ubr")}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* STEP 2 + 3 — Plan with Ready / Upcoming tabs (RankAI style) */}
        <section className="mb-8 p-6 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="step2-plan">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: TH.accent }}>Step 2 · Plan</div>
              <h2 className="text-xl font-medium mt-1">Editorial calendar — next 120 days</h2>
            </div>
            <button onClick={runNow} disabled={!!busy || scheduledCount === 0} className="px-4 py-2 rounded-full text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-50" style={{ background: TH.green, color: TH.bg }} data-testid="run-now-btn">
              <PlayCircle size={14} weight="fill" /> {busy === "run" ? "WRITING…" : "PUBLISH NEXT NOW"}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-4 border-b flex-wrap" style={{ borderColor: TH.border }}>
            <button
              onClick={() => setTab("ready")}
              className="px-3 py-2 text-sm font-medium inline-flex items-center gap-2"
              style={{ color: tab === "ready" ? TH.accent : TH.textMute, borderBottom: tab === "ready" ? `2px solid ${TH.accent}` : "2px solid transparent" }}
              data-testid="tab-ready"
            >
              Ready <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: TH.surfaceHi, color: TH.text }}>{readyRows.length}</span>
            </button>
            <button
              onClick={() => setTab("upcoming")}
              className="px-3 py-2 text-sm font-medium inline-flex items-center gap-2"
              style={{ color: tab === "upcoming" ? TH.accent : TH.textMute, borderBottom: tab === "upcoming" ? `2px solid ${TH.accent}` : "2px solid transparent" }}
              data-testid="tab-upcoming"
            >
              Upcoming <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: TH.surfaceHi, color: TH.text }}>{upcomingRows.length}</span>
            </button>
            <button
              onClick={() => setTab("lowkd")}
              className="px-3 py-2 text-sm font-medium inline-flex items-center gap-2"
              style={{ color: tab === "lowkd" ? TH.accent : TH.textMute, borderBottom: tab === "lowkd" ? `2px solid ${TH.accent}` : "2px solid transparent" }}
              data-testid="tab-lowkd"
            >
              <Star size={12} weight="fill" /> Low KD (Easy Wins)
              <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: TH.surfaceHi, color: TH.text }}>{lowKdRows.length}</span>
            </button>
            <button
              onClick={() => setTab("custom")}
              className="px-3 py-2 text-sm font-medium inline-flex items-center gap-2"
              style={{ color: tab === "custom" ? TH.accent : TH.textMute, borderBottom: tab === "custom" ? `2px solid ${TH.accent}` : "2px solid transparent" }}
              data-testid="tab-custom"
            >
              <Sparkle size={12} weight="fill" /> Add Topic
              <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: TH.surfaceHi, color: TH.text }}>{customRows.length}</span>
            </button>
            {tab === "lowkd" && (
              <button onClick={generateLowKd} disabled={!!busy} className="ml-auto px-3 py-1 rounded-full text-[11px] font-bold tracking-wider inline-flex items-center gap-1 disabled:opacity-50" style={{ background: TH.purple + "20", color: TH.purple, border: `1px solid ${TH.purple + "60"}` }} data-testid="generate-lowkd-btn">
                <Sparkle size={11} weight="fill" /> {busy === "lowkd" ? "GENERATING…" : "GENERATE 300 LOW-KD"}
              </button>
            )}
          </div>

          {/* Custom-topic composer — shown ONLY when the Custom tab is active. */}
          {tab === "custom" && (
            <form
              onSubmit={publishCustomTopic}
              className="p-4 rounded-2xl border mb-4 space-y-3"
              style={{ background: TH.surfaceHi, borderColor: TH.border }}
              data-testid="custom-topic-composer"
            >
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: TH.accent }}>Compose a one-off post</div>
                <p className="text-xs" style={{ color: TH.textMute }}>
                  Type any topic and Haiku will write it in the same tone as every AutoBlog article (600+ words, 5 FAQs, canonical internal links, auto-sanitised). Cost: ~₹0.20 · Time: ~40s.
                </p>
              </div>
              <label className="block">
                <span className="text-[11px] font-semibold" style={{ color: TH.text }}>
                  Article title <span style={{ color: TH.textDim }}>(15–140 chars — this is the H1 and SEO title)</span>
                </span>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  maxLength={140}
                  placeholder="e.g. Rahu in the 10th House: Career Signals and Timing"
                  className="mt-1 w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ background: TH.bg, color: TH.text, border: `1px solid ${TH.border}` }}
                  data-testid="custom-topic-title-input"
                  autoFocus
                />
                <div className="text-[10px] mt-1 flex justify-between" style={{ color: TH.textDim }}>
                  <span>{customTitle.trim().length < 15 ? `at least ${15 - customTitle.trim().length} more chars needed` : "looks good"}</span>
                  <span>{customTitle.length}/140</span>
                </div>
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold" style={{ color: TH.text }}>
                  Angle / hook <span style={{ color: TH.textDim }}>(optional — biases the tone)</span>
                </span>
                <input
                  type="text"
                  value={customAngle}
                  onChange={(e) => setCustomAngle(e.target.value)}
                  maxLength={240}
                  placeholder="e.g. for a first-generation entrepreneur without any inherited business"
                  className="mt-1 w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ background: TH.bg, color: TH.text, border: `1px solid ${TH.border}` }}
                  data-testid="custom-topic-angle-input"
                />
              </label>
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="submit"
                  disabled={!!busy || customTitle.trim().length < 15}
                  className="px-4 py-2 rounded-full text-[12px] font-bold tracking-wider uppercase inline-flex items-center gap-2 disabled:opacity-40"
                  style={{ background: TH.accent, color: TH.bg }}
                  data-testid="custom-topic-publish-btn"
                >
                  <Sparkle size={13} weight="fill" />
                  {busy === "custom" ? "PUBLISHING…" : "GENERATE + PUBLISH"}
                </button>
                <span className="text-[11px]" style={{ color: TH.textDim }}>
                  ~₹0.20 · ~40s · lands under /blog/&lt;auto-slug&gt;
                </span>
              </div>
            </form>
          )}

          {/* Bulk-actions toolbar */}
          {rowsForTab.length > 0 && (
            <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="bulk-toolbar">
              <span className="text-[11px]" style={{ color: TH.textMute }}>
                {selected.size > 0 ? `${selected.size} selected` : "Select rows to bulk-publish"}
              </span>
              <button
                disabled={!!busy || selected.size === 0}
                onClick={() => bulkPublish("selected")}
                className="px-3 py-1 rounded-full text-[11px] font-bold tracking-wider inline-flex items-center gap-1 disabled:opacity-40"
                style={{ background: TH.green, color: TH.bg }}
                data-testid="bulk-publish-selected-btn"
              >
                <PlayCircle size={11} weight="fill" /> PUBLISH SELECTED ({selected.size})
              </button>
              <button
                disabled={!!busy || rowsForTab.length === 0}
                onClick={() => bulkPublish("tab")}
                className="px-3 py-1 rounded-full text-[11px] font-bold tracking-wider inline-flex items-center gap-1 disabled:opacity-40"
                style={{ background: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}` }}
                data-testid="bulk-publish-tab-btn"
              >
                PUBLISH ALL IN TAB ({rowsForTab.length})
              </button>
              <button
                disabled={!!busy || scheduledCount === 0}
                onClick={() => bulkPublish("all")}
                className="px-3 py-1 rounded-full text-[11px] font-bold tracking-wider inline-flex items-center gap-1 disabled:opacity-40"
                style={{ background: TH.surfaceHi, color: TH.textMute, border: `1px solid ${TH.border}` }}
                data-testid="bulk-publish-all-btn"
              >
                PUBLISH EVERYTHING PLANNED ({scheduledCount})
              </button>
            </div>
          )}

          {rowsForTab.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: TH.textDim }}>
              {tab === "ready"
                ? "Nothing is overdue or queued for today. The scheduler will pick the first 'Upcoming' row at 02:00 UTC."
                : tab === "custom"
                ? "No custom topics yet. Use the composer above to write one on demand."
                : tab === "lowkd"
                ? "No Low-KD candidates yet. Click 'GENERATE 300 LOW-KD' above to fill the pool."
                : "Nothing scheduled yet. Click 'Generate 120-day plan' above."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ color: TH.text }}>
                <thead className="text-[10px] tracking-[0.18em] uppercase" style={{ color: TH.textMute, borderBottom: `1px solid ${TH.border}` }}>
                  <tr>
                    <th className="text-left py-2 pr-2 w-8">
                      <input
                        type="checkbox"
                        checked={rowsForTab.length > 0 && rowsForTab.every((r) => selected.has(r.plan_id))}
                        onChange={toggleAllInTab}
                        data-testid="select-all-checkbox"
                        aria-label="Select all rows in this tab"
                      />
                    </th>
                    <th className="text-left py-2 pr-3 w-20">Week</th>
                    <th className="text-left py-2 pr-3 w-28">Date</th>
                    <th className="text-left py-2 pr-3">Keyword</th>
                    <th className="text-left py-2 pr-3 w-32">Search Volume</th>
                    <th className="text-left py-2 pr-3">Title</th>
                    <th className="text-left py-2 pr-3 w-24">Status</th>
                    <th className="text-right py-2 w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsForTab.map((row, idx) => {
                    const isEasy = (row.kd != null && row.kd < 20) && (row.volume != null && row.volume >= 2000);
                    return (
                    <tr key={row.plan_id} style={{ borderBottom: `1px solid ${TH.border}` }} data-testid={`plan-row-${row.scheduled_date || row.plan_id}`}>
                      <td className="py-2 pr-2">
                        <input
                          type="checkbox"
                          checked={selected.has(row.plan_id)}
                          onChange={() => toggleOne(row.plan_id)}
                          data-testid={`row-checkbox-${row.plan_id}`}
                          aria-label={`Select ${row.title}`}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}` }}>
                          {row.priority === "low_kd" ? "Pool" : row.priority === "custom" ? "Custom" : `Week ${weekFromIndex(idx)}`}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs" style={{ color: TH.textMute }}>{row.scheduled_date || <span style={{ color: TH.textDim }}>unscheduled</span>}</td>
                      <td className="py-2 pr-3 text-xs">
                        {row.source_keyword ? (
                          <span style={{ color: TH.text }}>{row.source_keyword}</span>
                        ) : (
                          <span style={{ color: TH.textDim }}>—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs inline-flex items-center gap-1" style={{ color: row.volume ? TH.accent : TH.textDim }} data-testid={`plan-vol-${row.plan_id}`}>
                        {fmtVolume(row.volume)}
                        {isEasy && (
                          <span title={`Easy win — KD=${row.kd} (low) + ${fmtVolume(row.volume)} volume`} className="ml-1" data-testid={`star-${row.plan_id}`}>
                            <Star size={12} weight="fill" style={{ color: TH.purple }} />
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {row.slug ? (
                          <Link to={`/blog/${row.slug}`} target="_blank" style={{ color: TH.accent }}>{row.title}</Link>
                        ) : (
                          <span title={row.angle}>{row.title}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: STATUS_COLOR[row.status] || TH.textDim }}>
                          {row.status}
                        </span>
                      </td>
                      <td className="py-2 text-right">
                        {row.status === "planned" && (
                          <button onClick={() => skipPlan(row.plan_id)} className="text-[11px] underline" style={{ color: TH.textMute }} data-testid={`skip-plan-${row.scheduled_date}`}>
                            skip
                          </button>
                        )}
                        {row.status === "published" && row.slug && row.post_id && (
                          <button
                            onClick={() => openEdit(row)}
                            disabled={busy.startsWith("edit:")}
                            className="text-[11px] underline disabled:opacity-40"
                            style={{ color: TH.accent }}
                            data-testid={`edit-post-${row.post_id}`}
                            title="Ask Haiku to make a specific edit to this post"
                          >
                            {busy === `edit:${row.post_id}` ? "editing…" : "edit"}
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Recently published */}
        {(status?.recent_published || []).length > 0 && (
          <section className="p-6 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="autoblog-recent">
            <h2 className="text-xl font-medium mb-3 flex items-center gap-2"><ArticleIcon size={20} /> Recently published</h2>
            <ul className="text-sm space-y-2">
              {status.recent_published.map((p) => (
                <li key={p.plan_id || p.slug} className="flex items-center justify-between gap-3">
                  <Link to={`/blog/${p.slug}`} target="_blank" style={{ color: TH.accent }} className="truncate">{p.title}</Link>
                  <span className="text-[11px] shrink-0" style={{ color: TH.textMute }}>{p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button onClick={refresh} className="text-xs inline-flex items-center gap-1" style={{ color: TH.textMute }}>
            <ArrowsClockwise size={12} /> refresh state
          </button>
        </div>
          </>
        )}

        {/* AI-edit modal — opens when the admin clicks "edit" on any published row.
            Surgical: pass a free-form instruction to Haiku, apply, refresh. */}
        {editTarget && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
            data-testid="edit-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setEditTarget(null); }}
          >
            <div
              className="w-full max-w-lg rounded-2xl border p-5 space-y-4"
              style={{ background: TH.bg, borderColor: TH.border, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
            >
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: TH.accent }}>Edit an already-published post</div>
                <h3 className="text-lg font-medium truncate" style={{ color: TH.text }}>{editTarget.title}</h3>
                <a href={`/blog/${editTarget.slug}`} target="_blank" rel="noopener noreferrer" className="text-[11px] underline" style={{ color: TH.textMute }}>
                  /blog/{editTarget.slug} ↗
                </a>
              </div>
              <label className="block">
                <span className="text-[11px] font-semibold" style={{ color: TH.text }}>
                  What should be changed? <span style={{ color: TH.textDim }}>(8–800 chars — be specific)</span>
                </span>
                <textarea
                  value={editInstruction}
                  onChange={(e) => setEditInstruction(e.target.value)}
                  maxLength={800}
                  rows={4}
                  placeholder='e.g. "Integrate our compatibility tool hyperlink for marriage compatibility naturally in the section on partner traits." or "Soften the tone in the intro." or "Add a paragraph about Mars in the 7th house."'
                  className="mt-1 w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ background: TH.surface, color: TH.text, border: `1px solid ${TH.border}` }}
                  data-testid="edit-instruction-input"
                  autoFocus
                />
                <div className="text-[10px] mt-1 flex justify-between" style={{ color: TH.textDim }}>
                  <span>Haiku only changes the parts your instruction covers; the rest stays untouched.</span>
                  <span>{editInstruction.length}/800</span>
                </div>
              </label>
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => setEditTarget(null)}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                  style={{ background: "transparent", color: TH.textMute, border: `1px solid ${TH.border}` }}
                  data-testid="edit-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={submitEdit}
                  disabled={editInstruction.trim().length < 8 || busy.startsWith("edit:")}
                  className="px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40"
                  style={{ background: TH.accent, color: TH.bg }}
                  data-testid="edit-apply-btn"
                >
                  <Sparkle size={11} weight="fill" />
                  {busy.startsWith("edit:") ? "Editing…" : "Apply edit"}
                </button>
              </div>
              <p className="text-[10px]" style={{ color: TH.textDim }}>
                ~₹0.05 · ~10s · original body is snapshotted to `blog_rewrites` before overwrite.
              </p>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
